import { createContext, useContext, useState, useCallback } from 'react';
import * as api from '../lib/api';

// One global paywall modal instead of every gated call site (VoiceInput,
// Settings' Gmail connect/sync, Chat) rendering its own — before this,
// hitting a 402 just showed a toast with no way to act on it, which is the
// single worst moment to lose a conversion (the user just tried to use the
// feature they'd pay for). openPaywall(reason) from anywhere; the reason
// string is the backend's own 402 detail message, already written to sell
// the specific feature that got blocked.
//
// mustAcknowledge (added 2026-09-17 for the proactive trial-urgency popup —
// see App.js): when true, the modal can't be dismissed via the X, the
// backdrop, or Escape — only by clicking "Actualizar a Pro" or "Entiendo,
// seguir sin Pro", and that click is recorded server-side
// (profile.trial_urgency_acknowledged_at) so /admin can see who's actually
// seen it. The reactive 402 case never sets this — blocking someone out of
// the whole app over a single locked feature would be overkill.
const PaywallContext = createContext({ openPaywall: () => {} });

export function PaywallProvider({ children }) {
  const [state, setState] = useState(null); // null = closed, else {reason, mustAcknowledge}
  const [busy, setBusy] = useState(false);

  const openPaywall = useCallback((message, opts) => {
    setState({ reason: message || 'Esta función es parte de MiPisto Pro.', mustAcknowledge: !!opts?.mustAcknowledge });
  }, []);
  const closePaywall = useCallback(() => setState(null), []);

  const acknowledge = useCallback((action = 'dismiss') => {
    api.acknowledgeTrialUrgency(action).catch(() => {}); // best effort — never block the UI on this
    setState(null);
  }, []);

  const upgrade = useCallback(async () => {
    setBusy(true);
    try {
      const { url } = await api.createCheckout();
      window.location.href = url;
    } catch (e) {
      setBusy(false);
      throw e; // let PaywallModal toast it — keeps this context UI-agnostic
    }
  }, []);

  return (
    <PaywallContext.Provider value={{
      openPaywall, closePaywall, acknowledge, busy, upgrade,
      reason: state?.reason ?? null, mustAcknowledge: !!state?.mustAcknowledge,
    }}>
      {children}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  return useContext(PaywallContext);
}
